import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LayoutDashboard, Users, HardDrive, LogOut, Menu, X, AlertTriangle, ClipboardList, Settings, Send, SlidersHorizontal } from 'lucide-react';
import { useState, useEffect, type ReactNode } from 'react';
import { storage, type R2Usage } from '../lib/api';

interface Props {
  children: ReactNode;
}

const navItems = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/onboarding', icon: ClipboardList, label: 'Altas' },
  { to: '/clients', icon: Users, label: 'Tenants' },
  { to: '/storage', icon: HardDrive, label: 'Almacenamiento' },
  { to: '/settings', icon: Settings, label: 'Ajustes' },
  { to: '/outreach/review', icon: Send, label: 'Outreach' },
  { to: '/outreach/settings', icon: SlidersHorizontal, label: 'Outreach Config' },
];

export default function AdminLayout({ children }: Props) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [r2Usage, setR2Usage] = useState<R2Usage | null>(null);

  useEffect(() => {
    storage.usage().then(d => setR2Usage(d.usage)).catch(() => {});
    // Re-check every 5 minutes
    const interval = setInterval(() => {
      storage.usage().then(d => setR2Usage(d.usage)).catch(() => {});
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const r2Alerts = r2Usage?.alerts ?? [];
  const maxPct = r2Usage ? Math.max(r2Usage.percentages.storage, r2Usage.percentages.class_a, r2Usage.percentages.class_b) : 0;

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen flex bg-gray-50">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:static inset-y-0 left-0 z-40 w-64 bg-teal-900 text-white
          transform transition-transform duration-200 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0
          flex flex-col
        `}
      >
        {/* Logo */}
        <div className="p-6 border-b border-teal-700/50">
          <div className="flex items-center justify-between">
            <div>
              <img src="/kanarix-logo-white.png" alt="Kanarix" className="h-16" />
            </div>
            <button
              className="lg:hidden text-teal-300 hover:text-white"
              onClick={() => setSidebarOpen(false)}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-teal-700/60 text-white'
                    : 'text-teal-200 hover:bg-teal-800 hover:text-white'
                }`
              }
            >
              <item.icon size={18} />
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* User */}
        <div className="p-4 border-t border-teal-700/50">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-teal-600 flex items-center justify-center text-sm font-bold">
              {user?.name.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-teal-300 truncate">{user?.email}</p>
            </div>
            <button
              onClick={handleLogout}
              className="text-teal-300 hover:text-white transition-colors"
              title="Cerrar sesión"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar (mobile) */}
        <header className="lg:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => setSidebarOpen(true)}
            className="text-gray-600 hover:text-teal-700"
          >
            <Menu size={24} />
          </button>
          <img src="/kanarix-logo-dark.png" alt="Kanarix" className="h-12" />
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 md:p-6 lg:p-8 overflow-auto">
          {r2Alerts.length > 0 && (
            <div className={`mb-4 p-3 rounded-lg flex items-center gap-3 text-sm ${
              maxPct >= 90
                ? 'bg-red-50 border border-red-200 text-red-800'
                : maxPct >= 75
                ? 'bg-amber-50 border border-amber-200 text-amber-800'
                : 'bg-yellow-50 border border-yellow-200 text-yellow-800'
            }`}>
              <AlertTriangle size={18} className="flex-shrink-0" />
              <span className="flex-1">
                <strong>R2 Storage:</strong>{' '}
                {maxPct >= 90
                  ? `¡${maxPct}% del límite gratuito alcanzado! Riesgo de cargos.`
                  : maxPct >= 75
                  ? `${maxPct}% del límite gratuito usado. Revisa tu almacenamiento.`
                  : `${maxPct}% del límite gratuito. Todo bajo control.`}
              </span>
              <NavLink
                to="/storage"
                className="text-xs font-medium underline whitespace-nowrap"
              >
                Ver manager →
              </NavLink>
            </div>
          )}
          {children}
        </main>
      </div>
    </div>
  );
}
